import React, { useState } from 'react';
import { Save, Clock, RotateCcw, ChevronDown, ChevronUp, Crown, Plus, Check, Trash2, AlertCircle, Palette } from 'lucide-react';
import { useSectionStyles, SectionStyle } from '../../hooks/useSectionStyles';
import { useStyleProfiles } from '../../hooks/useStyleProfiles';
import { PremiumFeature } from '../PremiumFeature';
import { PRESET_THEMES, loadCustomColors, saveCustomColors, loadSelectedTheme, saveSelectedTheme, buildCustomTheme, type StudyPageThemeId, type CustomColorConfig } from '../../lib/studyPageThemes';

interface AutosaveConfig {
  enabled: boolean;
  interval: number;
}

interface SettingsSection {
  id: string;
  title: string;
  expanded: boolean;
}

const colorOptions = [
  { label: 'Crimson', value: 'crimson' },
  { label: 'Electric', value: 'electric' },
  { label: 'Emerald', value: 'emerald' },
  { label: 'Gold', value: 'gold' },
  { label: 'Fiery', value: 'fiery' },
  { label: 'Slate', value: 'slate' },
  { label: 'Midnight', value: 'midnight' }
];

export function SermonSettings() {
  const [sections, setSections] = useState<SettingsSection[]>(() => {
    const saved = localStorage.getItem('sermon_settings_sections');
    const defaultSections = [
      { id: 'autosave', title: 'Autosave Settings', expanded: true },
      { id: 'studytheme', title: 'Study Page Theme', expanded: true },
      { id: 'sections', title: 'Section Styles', expanded: true }
    ];
    if (saved) {
      const parsed = JSON.parse(saved) as SettingsSection[];
      if (!parsed.find(s => s.id === 'studytheme')) {
        parsed.splice(1, 0, { id: 'studytheme', title: 'Study Page Theme', expanded: true });
      }
      return parsed;
    }
    return defaultSections;
  });

  const [autosaveConfig, setAutosaveConfig] = useState<AutosaveConfig>(() => {
    const saved = localStorage.getItem('sermon_autosave_config');
    return saved ? JSON.parse(saved) : {
      enabled: true,
      interval: 30000 // 30 seconds default
    };
  });

  const { styles, updateStyle, resetStyles } = useSectionStyles();
  const { profiles, loading: profilesLoading, error: profilesError, saveProfile, deleteProfile } = useStyleProfiles();
  const [showSaveProfile, setShowSaveProfile] = useState(false);
  const [newProfileName, setNewProfileName] = useState('');
  const [makeDefault, setMakeDefault] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [studyTheme, setStudyTheme] = useState<StudyPageThemeId>(loadSelectedTheme());
  const [customColors, setCustomColors] = useState<CustomColorConfig>(loadCustomColors());
  const [themeSaved, setThemeSaved] = useState(false);

  const handleThemeChange = (themeId: StudyPageThemeId) => {
    setStudyTheme(themeId);
    saveSelectedTheme(themeId);
    setThemeSaved(true);
    setTimeout(() => setThemeSaved(false), 2000);
  };

  const handleCustomColorChange = (field: keyof CustomColorConfig, value: string) => {
    setCustomColors(prev => {
      const updated = { ...prev, [field]: value };
      saveCustomColors(updated);
      return updated;
    });
  };

  const handleConfigChange = (changes: Partial<AutosaveConfig>) => {
    setAutosaveConfig(prev => {
      const updated = { ...prev, ...changes };
      localStorage.setItem('sermon_autosave_config', JSON.stringify(updated));
      return updated;
    });
  };

  const handleStyleChange = (type: string, field: keyof SectionStyle, value: string) => {
    updateStyle(type, { [field]: value });
  };

  const toggleSection = (sectionId: string) => {
    setSections(prev => {
      const updated = prev.map(section => 
        section.id === sectionId 
          ? { ...section, expanded: !section.expanded }
          : section
      );
      localStorage.setItem('sermon_settings_sections', JSON.stringify(updated));
      return updated;
    });
  };

  const isExpanded = (sectionId: string) => {
    return sections.find(s => s.id === sectionId)?.expanded ?? true;
  };

  const handleSaveProfile = async () => {
    if (!newProfileName.trim()) {
      setSaveError('Please enter a profile name');
      return;
    }

    try {
      setSaveError(null);
      await saveProfile(newProfileName.trim(), styles, makeDefault);
      setShowSaveProfile(false);
      setNewProfileName('');
      setMakeDefault(false);
    } catch (err) {
      console.error('Error saving profile:', err);
      setSaveError(err instanceof Error ? err.message : 'Failed to save profile');
    }
  };

  const handleApplyProfile = (profile: typeof profiles[0]) => {
    profile.styles.forEach(style => {
      updateStyle(style.type, {
        backgroundColor: style.backgroundColor,
        textColor: style.textColor,
        borderColor: style.borderColor
      });
    });
  };

  return (
    <div className="space-y-8">
      {/* Autosave Settings */}
      <div className="bg-white rounded-lg shadow-sm border border-sand/20 overflow-hidden">
        <div 
          className="p-6 border-b border-sand/20 cursor-pointer hover:bg-sand/5"
          onClick={() => toggleSection('autosave')}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Save className="h-5 w-5 text-midnight" />
              <h2 className="text-lg font-semibold text-midnight">Autosave Settings</h2>
            </div>
            {isExpanded('autosave') ? (
              <ChevronUp className="h-5 w-5 text-slate" />
            ) : (
              <ChevronDown className="h-5 w-5 text-slate" />
            )}
          </div>
        </div>
        
        {isExpanded('autosave') && (
          <div className="p-6 space-y-6">
            <div className="flex items-center space-x-4">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={autosaveConfig.enabled}
                  onChange={(e) => handleConfigChange({ enabled: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-sand/20 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-electric/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-sand after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-electric"></div>
                <span className="ml-3 text-sm font-medium text-midnight">Enable Autosave</span>
              </label>
            </div>

            {autosaveConfig.enabled && (
              <>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-slate">Save Interval</label>
                  <select
                    value={autosaveConfig.interval}
                    onChange={(e) => handleConfigChange({ interval: parseInt(e.target.value) })}
                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-sand rounded-lg focus:outline-none focus:ring-electric focus:border-electric sm:text-sm"
                  >
                    <option value={15000}>15 seconds</option>
                    <option value={30000}>30 seconds</option>
                    <option value={60000}>1 minute</option>
                    <option value={300000}>5 minutes</option>
                  </select>
                  <p className="text-xs text-slate mt-1">
                    This setting controls both how often your content is saved and how often you'll see save notifications.
                  </p>
                </div>

                <div className="bg-sand/10 rounded-lg p-4 text-sm text-slate">
                  <div className="flex items-center mb-2">
                    <Clock className="h-4 w-4 mr-2 text-electric" />
                    <span className="font-medium text-midnight">How Autosave Works</span>
                  </div>
                  <p>
                    Your sermon will be automatically saved every {autosaveConfig.interval / 1000} seconds after you stop typing.
                  </p>
                  <p className="mt-2">
                    To minimize distractions, save notifications will only appear once per interval, even though your work is being saved continuously.
                  </p>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Study Page Theme */}
      <div className="bg-white rounded-lg shadow-sm border border-sand/20 overflow-hidden">
        <div 
          className="p-6 border-b border-sand/20 cursor-pointer hover:bg-sand/5"
          onClick={() => toggleSection('studytheme')}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Palette className="h-5 w-5 text-midnight" />
              <h2 className="text-lg font-semibold text-midnight">Study Page Theme</h2>
              {themeSaved && <span className="text-xs text-emerald font-medium">Saved!</span>}
            </div>
            {isExpanded('studytheme') ? (
              <ChevronUp className="h-5 w-5 text-slate" />
            ) : (
              <ChevronDown className="h-5 w-5 text-slate" />
            )}
          </div>
        </div>
        {isExpanded('studytheme') && (
          <div className="p-6">
            <p className="text-sm text-slate mb-4">Choose a default theme for shared study pages. You can also pick a theme when sharing.</p>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              {PRESET_THEMES.map((theme) => {
                const previewColors = theme.id === 'custom' ? buildCustomTheme(customColors) : theme.colors;
                return (
                  <button
                    key={theme.id}
                    onClick={() => handleThemeChange(theme.id)}
                    className={`relative rounded-xl border-2 p-1 transition-all ${
                      studyTheme === theme.id 
                        ? 'border-electric shadow-md' 
                        : 'border-sand/30 hover:border-sand/60'
                    }`}
                  >
                    <div 
                      className="rounded-lg p-4 h-32 flex flex-col justify-between"
                      style={{ backgroundColor: previewColors.background }}
                    >
                      <div>
                        <div className="h-2 w-16 rounded mb-1" style={{ backgroundColor: previewColors.textPrimary, opacity: 0.8 }} />
                        <div className="h-1.5 w-24 rounded" style={{ backgroundColor: previewColors.textMuted, opacity: 0.5 }} />
                      </div>
                      <div className="flex gap-2">
                        <div className="h-10 flex-1 rounded-lg" style={{ backgroundColor: previewColors.cardBackground, border: `1px solid ${previewColors.cardBorder}` }}>
                          <div className="w-3 h-3 rounded m-2" style={{ backgroundColor: previewColors.accent, opacity: 0.6 }} />
                        </div>
                        <div className="h-10 flex-1 rounded-lg" style={{ backgroundColor: previewColors.cardBackground, border: `1px solid ${previewColors.cardBorder}` }}>
                          <div className="w-3 h-3 rounded m-2" style={{ backgroundColor: previewColors.accent, opacity: 0.6 }} />
                        </div>
                      </div>
                    </div>
                    <div className="px-2 py-2">
                      <div className="text-sm font-medium text-midnight">{theme.name}</div>
                      <div className="text-xs text-slate">{theme.description}</div>
                    </div>
                    {studyTheme === theme.id && (
                      <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-electric flex items-center justify-center">
                        <Check className="h-3 w-3 text-white" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {studyTheme === 'custom' && (
              <div className="border border-sand/30 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-midnight mb-4">Custom Colors</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate mb-1.5">Background</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={customColors.background}
                        onChange={(e) => handleCustomColorChange('background', e.target.value)}
                        className="h-9 w-12 rounded border border-sand cursor-pointer"
                      />
                      <input
                        type="text"
                        value={customColors.background}
                        onChange={(e) => handleCustomColorChange('background', e.target.value)}
                        className="flex-1 px-3 py-2 text-xs border border-sand rounded-lg font-mono"
                        placeholder="#1c1f2e"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate mb-1.5">Accent</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={customColors.accent}
                        onChange={(e) => handleCustomColorChange('accent', e.target.value)}
                        className="h-9 w-12 rounded border border-sand cursor-pointer"
                      />
                      <input
                        type="text"
                        value={customColors.accent}
                        onChange={(e) => handleCustomColorChange('accent', e.target.value)}
                        className="flex-1 px-3 py-2 text-xs border border-sand rounded-lg font-mono"
                        placeholder="#a88b5d"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate mb-1.5">Text</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={customColors.textPrimary}
                        onChange={(e) => handleCustomColorChange('textPrimary', e.target.value)}
                        className="h-9 w-12 rounded border border-sand cursor-pointer"
                      />
                      <input
                        type="text"
                        value={customColors.textPrimary}
                        onChange={(e) => handleCustomColorChange('textPrimary', e.target.value)}
                        className="flex-1 px-3 py-2 text-xs border border-sand rounded-lg font-mono"
                        placeholder="#f5f0e8"
                      />
                    </div>
                  </div>
                </div>
                <div className="mt-4">
                  <p className="text-xs text-slate mb-2">Preview</p>
                  <div 
                    className="rounded-xl p-6 border"
                    style={{ 
                      backgroundColor: buildCustomTheme(customColors).background, 
                      borderColor: buildCustomTheme(customColors).cardBorder 
                    }}
                  >
                    <div className="flex gap-3 mb-3">
                      <div 
                        className="w-8 h-8 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: buildCustomTheme(customColors).accentSubtle }}
                      >
                        <Palette className="h-4 w-4" style={{ color: buildCustomTheme(customColors).accent }} />
                      </div>
                      <div>
                        <div className="text-sm font-semibold" style={{ color: buildCustomTheme(customColors).textPrimary, fontFamily: 'Playfair Display, serif' }}>Sermon Title</div>
                        <div className="text-xs" style={{ color: buildCustomTheme(customColors).textMuted }}>Preview of your custom theme</div>
                      </div>
                    </div>
                    <div 
                      className="rounded-lg p-3"
                      style={{ 
                        backgroundColor: buildCustomTheme(customColors).cardBackground,
                        border: `1px solid ${buildCustomTheme(customColors).cardBorder}`
                      }}
                    >
                      <div className="text-xs" style={{ color: buildCustomTheme(customColors).textSecondary }}>
                        This is how your study page cards will look with these colors.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Section Styles */}
      <div className="bg-white rounded-lg shadow-sm border border-sand/20 overflow-hidden">
        <div 
          className="p-6 border-b border-sand/20 cursor-pointer hover:bg-sand/5"
          onClick={() => toggleSection('sections')}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Save className="h-5 w-5 text-midnight" />
              <div className="flex items-center space-x-2">
                <h2 className="text-lg font-semibold text-midnight">Section Styles</h2>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gold/10 text-gold">
                  <Crown className="h-3 w-3 mr-1" />
                  Premium
                </span>
              </div>
            </div>
            {isExpanded('sections') ? (
              <ChevronUp className="h-5 w-5 text-slate" />
            ) : (
              <ChevronDown className="h-5 w-5 text-slate" />
            )}
          </div>
        </div>
        {isExpanded('sections') && (
          <PremiumFeature feature="advanced_formatting">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="text-sm text-slate">Set the styles for Preach Mode.</div>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => setShowSaveProfile(true)}
                    className="flex items-center px-3 py-1.5 text-sm text-electric hover:text-electric/80"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Save as Profile
                  </button>
                  <button
                    onClick={resetStyles}
                    className="flex items-center px-3 py-1.5 text-sm text-slate hover:text-midnight"
                  >
                    <RotateCcw className="h-4 w-4 mr-1" />
                    Reset to Default
                  </button>
                </div>
              </div>

              {/* Save Profile Dialog */}
              {showSaveProfile && (
                <div className="mb-6 p-4 bg-sand/10 rounded-lg">
                  <h3 className="text-lg font-medium text-midnight mb-4">Save Style Profile</h3>
                  {saveError && (
                    <div className="mb-4 text-sm text-crimson bg-crimson/10 p-3 rounded-lg flex items-center">
                      <AlertCircle className="h-4 w-4 mr-2" />
                      {saveError}
                    </div>
                  )}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate mb-1">Profile Name</label>
                      <input
                        type="text"
                        value={newProfileName}
                        onChange={(e) => setNewProfileName(e.target.value)}
                        className="w-full px-3 py-2 border border-sand rounded-lg focus:ring-2 focus:ring-electric focus:border-transparent"
                        placeholder="Enter profile name..."
                      />
                    </div>
                    <div className="flex items-center">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={makeDefault}
                          onChange={(e) => setMakeDefault(e.target.checked)}
                          className="rounded border-sand text-electric focus:ring-electric"
                        />
                        <span className="ml-2 text-sm text-slate">Make Default Profile</span>
                      </label>
                    </div>
                    <div className="flex justify-end space-x-3">
                      <button
                        onClick={() => {
                          setShowSaveProfile(false);
                          setNewProfileName('');
                          setMakeDefault(false);
                          setSaveError(null);
                        }}
                        className="px-3 py-2 text-slate hover:text-midnight"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveProfile}
                        disabled={!newProfileName.trim()}
                        className="px-3 py-2 bg-electric text-white rounded-lg hover:bg-electric/90 disabled:opacity-50"
                      >
                        Save Profile
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Saved Profiles */}
              {profiles.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-slate mb-2">Saved Profiles</h3>
                  <div className="space-y-2">
                    {profiles.map(profile => (
                      <div
                        key={profile.id}
                        className="flex items-center justify-between p-3 bg-sand/5 rounded-lg border border-sand/20"
                      >
                        <div className="flex items-center">
                          <span className="font-medium text-midnight">{profile.name}</span>
                          {profile.is_default && (
                            <span className="ml-2 text-xs text-emerald bg-emerald/10 px-2 py-0.5 rounded-full">
                              Default
                            </span>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleApplyProfile(profile)}
                            className="p-2 text-slate hover:text-electric rounded-lg"
                            title="Apply Profile"
                          >
                            <Check className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => deleteProfile(profile.id)}
                            className="p-2 text-slate hover:text-crimson rounded-lg"
                            title="Delete Profile"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Dark Mode Override Option */}
              <div className="mb-6 p-4 bg-sand/10 rounded-lg">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-midnight">Dark Mode Behavior</h3>
                    <p className="text-sm text-slate mt-1">Control how section styles interact with dark mode</p>
                  </div>
                  <div className="flex items-center ml-4">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        defaultChecked={true}
                        onChange={(e) => {
                          document.documentElement.style.setProperty(
                            '--respect-dark-mode',
                            e.target.checked ? '1' : '0'
                          );
                        }}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-sand/20 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-electric/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-sand after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-electric"></div>
                      <span className="ml-3 text-sm font-medium text-midnight whitespace-nowrap">Adapt to Dark Mode</span>
                    </label>
                  </div>
                </div>
                <p className="text-xs text-slate mt-3">
                  When enabled, sermon sections will automatically adjust their colors in dark mode while preserving your custom styling in light mode.
                </p>
              </div>

              {/* Style Editor */}
              <div className="space-y-6">
                {/* Debug: Show number of styles */}
                <div className="text-xs text-slate mb-2">
                  Loaded {styles.length} section styles
                </div>
                {styles.map((style) => (
                  <div key={style.type} className="space-y-4">
                    <h3 className="text-lg font-medium text-midnight capitalize">
                      {style.type === 'main-point' ? 'Main Point' : 
                       style.type === 'custom' ? 'Custom' :
                       style.type.replace('-', ' ')}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate mb-1">
                          Background Color
                        </label>
                        <select
                          value={style.backgroundColor.split('-')[1].split('/')[0]}
                          onChange={(e) => handleStyleChange(
                            style.type,
                            'backgroundColor',
                            `bg-${e.target.value}/10`
                          )}
                          className="w-full px-3 py-2 border border-sand rounded-lg focus:ring-2 focus:ring-electric focus:border-transparent"
                        >
                          {colorOptions.map(color => (
                            <option key={color.value} value={color.value}>
                              {color.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate mb-1">
                          Text Color
                        </label>
                        <select
                          value={style.textColor.split('-')[1]}
                          onChange={(e) => handleStyleChange(
                            style.type,
                            'textColor',
                            `text-${e.target.value}`
                          )}
                          className="w-full px-3 py-2 border border-sand rounded-lg focus:ring-2 focus:ring-electric focus:border-transparent"
                        >
                          {colorOptions.map(color => (
                            <option key={color.value} value={color.value}>
                              {color.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate mb-1">
                          Border Color
                        </label>
                        <select
                          value={style.borderColor.split('-')[1]}
                          onChange={(e) => handleStyleChange(
                            style.type,
                            'borderColor',
                            `border-${e.target.value}`
                          )}
                          className="w-full px-3 py-2 border border-sand rounded-lg focus:ring-2 focus:ring-electric focus:border-transparent"
                        >
                          {colorOptions.map(color => (
                            <option key={color.value} value={color.value}>
                              {color.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className={`p-4 rounded-lg ${style.backgroundColor} ${style.textColor} border-l-4 ${style.borderColor}`}>
                      Preview Text
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </PremiumFeature>
        )}
      </div>
    </div>
  );
}